// ────────────────────────────────────────────────────────────────────────────
// Which copy functions this organisation is offered, and how they start.
//
// Stored on the Directory `Organisation` as `tmsCopyFunctionsJson` and edited
// by Stratum support from the Copy Functions card on Central Configuration:
//
//   { "ordersCopySection": true, "ordersCopyQuantities": true, … }
//
// NOT THE RULE THE OTHER TOGGLES USE. Work View tabs, Plan features and the
// menu all read a missing key as ON. These switch on features that are still
// being built, so each setting has its OWN default, here — the one place to
// change it. A missing key, a null column or an unreadable string all mean
// "the default", so flipping a default later reaches every organisation that
// never chose and leaves alone those that did.
// ────────────────────────────────────────────────────────────────────────────

export interface CopyFunctions {
  /** New order → "Start from a previous order". */
  ordersCopySection: boolean;
  /** Order templates: "Save as template" and New order's Templates list. */
  ordersTemplates: boolean;

  // ── What "Start from a previous order" has ticked when an order is picked.
  // The user can still change every one of them; the cargo, its service tags
  // and the tasks are always copied and so have no setting.
  ordersCopyQuantities: boolean;
  ordersCopyTimeWindows: boolean;
  ordersCopyTaskNotes: boolean;
  ordersCopyBookings: boolean;
  ordersCopyTemperature: boolean;
  ordersCopyReferences: boolean;
  ordersCopyDocuments: boolean;
  /**
   * The status a copied order is created with: false → Draft (the default),
   * true → Open. A boolean because the column holds booleans only. Not a box
   * the user ticks — support sets it here.
   */
  ordersCopyStartsOpen: boolean;
}

export type CopyFunctionId = keyof CopyFunctions;
/** A feature itself — shown or hidden. */
export type CopyFeatureId = 'ordersCopySection' | 'ordersTemplates';
/** A box the orders copy starts ticked or not. */
export type OrdersCopyDefaultId = Exclude<CopyFunctionId, CopyFeatureId | 'ordersCopyStartsOpen'>;

/** The features themselves (shown / hidden), in the order the card lists them. */
export const COPY_FUNCTION_ORDER: readonly CopyFeatureId[] = [
  'ordersCopySection',
  'ordersTemplates',
];

/** The orders copy's ticked-by-default boxes, in the order the copy panel lists them. */
export const ORDERS_COPY_DEFAULT_ORDER: readonly OrdersCopyDefaultId[] = [
  'ordersCopyQuantities',
  'ordersCopyTimeWindows',
  'ordersCopyTaskNotes',
  'ordersCopyBookings',
  'ordersCopyTemperature',
  'ordersCopyReferences',
  'ordersCopyDocuments',
];

const ALL_IDS: readonly CopyFunctionId[] = [
  ...COPY_FUNCTION_ORDER,
  ...ORDERS_COPY_DEFAULT_ORDER,
  'ordersCopyStartsOpen',
];

/**
 * What an organisation gets until support says otherwise. The orders copy
 * section is SHOWN — the copy shipped; support can still hide it per
 * organisation. The ticks bring the job across (quantities, times, notes,
 * bookings, temperature) and leave what belongs to the one run (references,
 * documents). Unit numbers (Tank IDs / BIC codes) and seals are never copied —
 * next time is usually another unit. A copy starts as a Draft.
 */
export const COPY_FUNCTION_DEFAULTS: Readonly<CopyFunctions> = {
  ordersCopySection: true,
  ordersTemplates: true,
  ordersCopyQuantities: true,
  ordersCopyTimeWindows: true,
  ordersCopyTaskNotes: true,
  ordersCopyBookings: true,
  ordersCopyTemperature: true,
  ordersCopyReferences: false,
  ordersCopyDocuments: false,
  ordersCopyStartsOpen: false,
};

/**
 * Read the stored document into a complete record. Never throws: an
 * unreadable value is not worth a broken screen, and falls back to the
 * defaults — what an un-configured organisation gets anyway.
 */
export function parseCopyFunctions(json: string | null | undefined): CopyFunctions {
  const functions: CopyFunctions = { ...COPY_FUNCTION_DEFAULTS };
  if (!json) return functions;

  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return functions;
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return functions;

  const stored = raw as Record<string, unknown>;
  for (const id of ALL_IDS) {
    // Only an explicit boolean overrides the default; anything else is absent.
    if (typeof stored[id] === 'boolean') functions[id] = stored[id];
  }
  return functions;
}

/**
 * The wire value. Writes EVERY setting this build knows about, so a saved
 * organisation holds an explicit choice for each — the defaults only speak for
 * organisations that never saved.
 */
export function serialiseCopyFunctions(functions: CopyFunctions): string {
  return JSON.stringify(Object.fromEntries(ALL_IDS.map((id) => [id, functions[id] === true])));
}
